import React, { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, Pencil, Trash2, LayoutGrid } from 'lucide-react';
import { Menu, MenuItem, MenuLabel, MenuDivider } from '../ds';
import { cx, focusRing, motion } from '../ds/classes';
import { useAuth } from '../../context/AuthContext';

// Enough to hop between the songs you're working on; the rest are on Projects home
const MAX_OTHERS = 6;

/**
 * The project's name in the studio bar, opening its menu: its DAW, rename and delete, and the
 * other projects to switch to (loaded the first time it opens). `quiet` steps the name down to body
 * text from tablet width up, when the clip after it is the main thing in the bar.
 */
export const ProjectMenu = ({ project, onRename, onDelete, quiet = false }) => {
  const { authFetch } = useAuth();
  const [others, setOthers] = useState([]);
  const loaded = useRef(false);

  const loadOthers = async (open) => {
    if (!open || loaded.current) return;
    loaded.current = true;
    try {
      const projects = await authFetch('/projects');
      setOthers(projects.filter((p) => p.id !== project.id).slice(0, MAX_OTHERS));
    } catch {
      loaded.current = false; // try again next time; the menu still works without the list
    }
  };

  return (
    <Menu
      align="start"
      onOpenChange={loadOthers}
      className="min-w-0"
      trigger={(props) => (
        <button
          type="button"
          aria-label={`${project.name}, project menu`}
          className={cx(
            'max-w-full min-h-target-min px-space-2 sm:px-space-3 inline-flex items-center gap-1.5 rounded-radius-md bg-transparent text-ink transition-colors hover:bg-raised',
            motion,
            focusRing
          )}
          {...props}
        >
          <span className={cx('truncate font-display text-project', quiet && 'md:font-text md:text-body md:font-medium md:text-ink-soft')}>{project.name}</span>
          <ChevronDown size={16} strokeWidth={2.5} aria-hidden="true" className="flex-none text-muted" />
        </button>
      )}
    >
      <MenuLabel title={project.name}>{project.daw ? `${project.daw} · plugin moves use its plugins` : 'No DAW set'}</MenuLabel>
      <MenuItem icon={Pencil} onSelect={onRename}>Rename project</MenuItem>
      <MenuItem icon={Trash2} onSelect={onDelete}>Delete project</MenuItem>
      <MenuDivider />
      {others.length > 0 && <MenuLabel>Switch to</MenuLabel>}
      {others.map((other) => (
        <MenuItem key={other.id} as={Link} to={`/projects/${other.id}`}>{other.name}</MenuItem>
      ))}
      <MenuItem as={Link} to="/projects" icon={LayoutGrid}>All projects</MenuItem>
    </Menu>
  );
};
